import { t } from "i18next";
import moment from "moment";
import { ChangeEvent, useContext } from "react";
import { Col, Container, Form, Row } from "react-bootstrap";
import DatePicker, { DateObject } from "react-multi-date-picker";

import Icons from "@app/assets/Icons";
import SelectInput from "@app/components/inputs/Select";
import TextInput from "@app/components/inputs/Text";
import Toggle from "@app/components/inputs/Toggle";
import { InstallationLog } from "@app/types/VersionControl";
import { FormContext } from "@app/utils/contexts/Form.context";
import { formatDate } from "@app/utils/timeHelpers";
import "./index.scss";
interface SelectOption {
    value: string;
    label: string;
}

interface FormData {
    nextVersion: string;
    expiryDate: string;
    isActive: boolean;
}

const VersionManagementForm = (props: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    version: any;
    nextVersion: SelectOption[];
    onChange: (_name: string, _value: string | boolean) => void;
    editFormData: FormData;
}) => {
    const { version, nextVersion, onChange, editFormData } = props;
    const { setIsDirty } = useContext(FormContext);
    const isDeploymentInProgress =
        version?.upcommingVersionStatus?.toLowerCase() ===
        t("VERSION_MANAGEMENT.EDIT.STATUS.LABEL");

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setIsDirty(true);
        const { name, value } = e.target;
        onChange(name, value);
    };

    return (
        <Container fluid className='py-4 px-5'>
            <Row className='mb-4'>
                <Col xs={6}>
                    <TextInput
                        label={`${t("VERSION_MANAGEMENT.EDIT.NAME.LABEL")}`}
                        placeholder={t("VERSION_MANAGEMENT.EDIT.NAME.PLACEHOLDER")}
                        value={version?.branchName}
                        name='branchName'
                        error={""}
                        disabled={true}
                    />
                </Col>
                <Col xs={6}>
                    <TextInput
                        label={`${t("VERSION_MANAGEMENT.EDIT.CODE.LABEL")}`}
                        placeholder={t("VERSION_MANAGEMENT.EDIT.CODE.PLACEHOLDER")}
                        value={version?.branchCode}
                        name='branchCode'
                        error={""}
                        disabled={true}
                    />
                </Col>
            </Row>

            <Row className='mb-4'>
                <Col xs={6}>
                    <TextInput
                        label={`${t("VERSION_MANAGEMENT.EDIT.VERSION.LABEL")}`}
                        placeholder={t("VERSION_MANAGEMENT.EDIT.VERSION.PLACEHOLDER")}
                        value={version?.branchVersion}
                        name='branchVersion'
                        error={""}
                        disabled={true}
                    />
                </Col>
                <Col xs={6}>
                    <TextInput
                        label={`${t("VERSION_MANAGEMENT.EDIT.INSTALLATION_DATE.LABEL")}`}
                        placeholder={t("VERSION_MANAGEMENT.EDIT.INSTALLATION_DATE.PLACEHOLDER")}
                        value={version?.installedOn ? formatDate(version.installedOn) : ""}
                        name='installedOn'
                        error={""}
                        disabled={true}
                    />
                </Col>
            </Row>

            <Row className='mb-4'>
                <Col xs={6}>
                    <TextInput
                        label={`${t("VERSION_MANAGEMENT.EDIT.UPCOMINGSTATUS.LABEL")}`}
                        placeholder={t("VERSION_MANAGEMENT.EDIT.UPCOMINGSTATUS.PLACEHOLDER")}
                        value={version?.upcommingVersionStatus}
                        name='upcommingVersionStatus'
                        error={""}
                        disabled={true}
                    />
                </Col>
                <Col xs={6}>
                    <SelectInput
                        label={`${t("VERSION_MANAGEMENT.EDIT.NEXT_VERSION.LABEL")}`}
                        value={editFormData?.nextVersion}
                        onChange={handleChange}
                        name='nextVersion'
                        options={[
                            {
                                value: "",
                                label: t("VERSION_MANAGEMENT.EDIT.NEXT_VERSION.PLACEHOLDER"),
                            },
                            ...nextVersion,
                        ]}
                        error={""}
                        disabled={isDeploymentInProgress}
                    />
                    {isDeploymentInProgress && (
                        <div className='next-version-disabled-message'>
                            {t("VERSION_MANAGEMENT.EDIT.NEXT_VERSION.DISABLED_MESSAGE")}
                        </div>
                    )}
                </Col>
            </Row>
            <Row className='mb-4'>
                <Col xs={6}>
                    <div className='range-picker-container'>
                        <p className='range-picker-label'>
                            {t("VERSION_MANAGEMENT.ADD.EXPIRY_DATE.LABEL")}
                        </p>
                        <DatePicker
                            value={
                                editFormData?.expiryDate
                                    ? new DateObject(editFormData?.expiryDate)
                                    : null
                            }
                            onChange={(date: DateObject | null) => {
                                const DateValue = date ? date.format("YYYY-MM-DD") : "";
                                onChange("expiryDate", DateValue);
                            }}
                            placeholder='DD/MM/YYYY'
                            format='DD/MM/YYYY'
                            minDate={moment().startOf("day").toDate()}
                        />

                        <img
                            src={Icons.CalendarIcon}
                            alt='rangePickerIcon'
                            className='range-picker-icon'
                        />
                    </div>
                </Col>
                <Col xs={6}>
                    <Form.Group className='d-flex align-items-end gap-4 toggle-status-container'>
                        <Form.Label className='text-input__label'>
                            {t("VERSION_MANAGEMENT.ADD.STATUS.LABEL")}
                        </Form.Label>
                        <Toggle
                            isActive={editFormData?.isActive}
                            onToggle={() => onChange("isActive", !editFormData?.isActive)}
                            display={
                                editFormData?.isActive
                                    ? `${t("COMMON.ACTIVE")}`
                                    : `${t("COMMON.INACTIVE")}`
                            }
                        />
                    </Form.Group>
                </Col>
            </Row>
            <div className='pervious_versiontext'>Previous Version</div>
            <div className='version-log-container__header'>
                <span className='version-log-container__title'>Version</span>
                <span className='version-log-container__title'>Installed Date</span>
            </div>
            {version?.aksInstallLogDtos.map((items: InstallationLog, index: number) => (
                <div
                    className={`version-log-container__rowWrap ${
                        index % 2 === 0 ? "version-log-container__rowGrey" : ""
                    }`}
                    key={index}
                >
                    <span>{items.version}</span>
                    <span>{formatDate(items.createOn)}</span>
                </div>
            ))}
        </Container>
    );
};

export default VersionManagementForm;
